import Cookie from "universal-cookie";
import { useRef, useCallback, useEffect } from "react";

import { useUpdate } from "../hooks";
const cookies = new Cookie();

export function getCookieValue(name: string): string {
  return cookies.get(name);
}

/**
 * Set up a callback to be called when the cookie value changes.
 *
 * Returns a function that (if called) will remove the callback.
 */
export function monitorCookieValue(
  name: string,
  callback: (value: string) => void
): () => void {
  let cookieValue = getCookieValue(name);
  const handler = () => {
    const current = getCookieValue(name);
    if (current !== cookieValue) {
      cookieValue = current;
      callback(cookieValue);
    }
  };

  cookies.addChangeListener(handler);

  return () => cookies.removeChangeListener(handler);
}

export function useCookieValue(name: string): {
  value: string;
  setValue: (value: string) => void;
} {
  const update = useUpdate();
  const ref = useRef(getCookieValue(name));

  useEffect(() => {
    return monitorCookieValue(name, update);
  }, [name, update]);

  const setValue = useCallback(
    (value: string) => {
      cookies.set(name, value, { sameSite: "strict" });
      ref.current = value;
      update();
    },
    [update, name]
  );

  return { value: ref.current, setValue };
}
